import { usersMetrics } from "./definitions/users.js"
import { activityMetrics } from "./definitions/activity.js"
import { moduleUsageMetrics } from "./definitions/module-usage.js"
import { productsMetrics } from "./definitions/products.js"
import { applicationsMetrics } from "./definitions/applications.js"
import { packagesMetrics } from "./definitions/packages.js"
import { submissionsMetrics } from "./definitions/submissions.js"
import { checklistsMetrics } from "./definitions/checklists.js"
import { projectsMetrics } from "./definitions/projects.js"
import { changesMetrics } from "./definitions/changes.js"
import { watchlistsMetrics } from "./definitions/watchlists.js"
import { tagsMetrics } from "./definitions/tags.js"
import { reportsUsageMetrics } from "./definitions/reports-usage.js"
import { lifecycleMetrics } from "./definitions/lifecycle.js"
import { METRIC_DOMAINS, type MetricDefinition, type MetricDomain } from "./types.js"

export const DOMAIN_ORDER: readonly MetricDomain[] = METRIC_DOMAINS

export const DOMAIN_LABELS: Record<MetricDomain, string> = {
  users: "Users",
  activity: "Activity",
  moduleUsage: "Module Usage",
  products: "Products",
  applications: "Applications",
  packages: "Packages",
  submissions: "Tracking",
  checklists: "Checklists",
  projects: "Projects",
  changes: "CCP",
  watchlists: "Standards",
  tags: "DMS",
  reportsUsage: "Reports",
  lifecycle: "Lifecycle",
}

export const DOMAIN_SLUGS: Record<MetricDomain, string> = {
  users: "users",
  activity: "activity",
  moduleUsage: "module-usage",
  products: "products",
  applications: "applications",
  packages: "packages",
  submissions: "submissions",
  checklists: "checklists",
  projects: "projects",
  changes: "changes",
  watchlists: "watchlists",
  tags: "tags",
  reportsUsage: "reports-usage",
  lifecycle: "lifecycle",
}

export const catalog: readonly MetricDefinition[] = [
  ...usersMetrics,
  ...activityMetrics,
  ...moduleUsageMetrics,
  ...productsMetrics,
  ...applicationsMetrics,
  ...packagesMetrics,
  ...submissionsMetrics,
  ...checklistsMetrics,
  ...projectsMetrics,
  ...changesMetrics,
  ...watchlistsMetrics,
  ...tagsMetrics,
  ...reportsUsageMetrics,
  ...lifecycleMetrics,
]

export const byId: ReadonlyMap<string, MetricDefinition> = new Map(
  catalog.map((m) => [m.id, m]),
)

export const byDomain: ReadonlyMap<MetricDomain, readonly MetricDefinition[]> = new Map(
  DOMAIN_ORDER.map((d) => [d, catalog.filter((m) => m.domain === d)]),
)

export const byModule: ReadonlyMap<string, readonly MetricDefinition[]> = new Map(
  [...new Set(catalog.map((m) => m.srvModule))].map((mod) => [
    mod,
    catalog.filter((m) => m.srvModule === mod),
  ]),
)

export function requireMetric(id: string): MetricDefinition {
  const def = byId.get(id)
  if (!def) throw new Error(`unknown_metric:${id}`)
  return def
}

export function allowedWidgets(def: MetricDefinition): readonly string[] {
  return [def.defaultWidget, ...(def.altWidgets ?? [])]
}

export function catalogRevision(): string {
  const canonical = JSON.stringify(
    catalog.map((m) => [m.id, m.valueType, m.rollup, m.dims, m.srvModule]),
  )
  let hash = 0x811c9dc5
  for (let i = 0; i < canonical.length; i++) {
    hash ^= canonical.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `fnv1a:${hash.toString(16).padStart(8, "0")}`
}
